import { HomeURL } from "../Constants";
import { isDev } from "./DebugUtils";

// Get the target origin for postMessage from HomeURL
const getPostMessageTargetOrigin = (): string => {
  try {
    if (HomeURL) {
      const url = new URL(HomeURL);
      return url.origin;
    }
  } catch {
    // Fall through to default
  }
  // Fallback to current origin if HomeURL is not set
  return typeof window !== 'undefined' ? window.location.origin : '*';
};

const cls = (input: string) =>
  input
    .replace(/\s+/gm, " ")
    .split(" ")
    .filter((cond) => typeof cond === "string")
    .join(" ")
    .trim();

// RFC 5322 compliant email validation - allows valid formats like user+tag@sub.domain.com
const emailRegEx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (email: string) => emailRegEx.test(email);

const getDefaultLocale = () => {
  // not working in node
  const locales = window.navigator.languages;
  let languageCode = "en";
  if (locales.length && locales[0].startsWith("zh")) {
    languageCode = "zh";
  }
  return languageCode;
};

const getLanguageCode = () => {
  // todo
  return "en";
};

const getLanguage = () => {
  // todo
  return "en-US";
};

const getAgent = () => {
  // todo
  return "web";
};

const getInfoFormIntlPhoneNumber = (
  phone: string,
  countryCode: string
): {
  formatPhoneNumber: string;
  formatCountryCode: string;
} => {
  let formatPhone = phone;
  if (phone.startsWith("+")) {
    formatPhone = phone.substring(countryCode.length + 1, phone.length);
  }
  return {
    formatPhoneNumber: formatPhone,
    formatCountryCode: `+${countryCode}`,
  };
};

const isVideo = (url: string) => {
  return (
    url?.toLowerCase().endsWith(".mp4") ||
    url?.toLowerCase().endsWith(".webm") ||
    url?.toLowerCase().endsWith(".ogg")
  );
};

const isImage = (url: string) => {
  return (
    url?.toLowerCase().endsWith(".jpeg") ||
    url?.toLowerCase().endsWith(".png") ||
    url?.toLowerCase().endsWith(".webp") ||
    url?.toLowerCase().endsWith(".gif") ||
    url?.toLowerCase().endsWith(".avif")
  );
};

const setImageDimensions = (file: any) => {
  const img = new Image();
  img.onload = () => {
    Object.assign(file, {
      height: img.height,
      width: img.width,
    });
  };
  img.src = URL.createObjectURL(file);
  return file;
};

const setVideoDimensions = (file: any) => {
  const video = document.createElement("video");
  video.ondurationchange = () => {
    Object.assign(file, {
      height: video.videoHeight,
      width: video.videoWidth,
      playableDuration: video.duration,
    });
  };
  video.src = URL.createObjectURL(file);
  return file;
};

const calVideoRatio = (width: number, height: number) => {
  let ratio = 1;
  if (width > 0 && height > 0) {
    ratio = width / height;
    if (ratio < 0.7) {
      // to avoid the layout too high
      ratio = 0.7;
    }
  }
  return `${ratio}`;
};

const emitLoginSuccess = async (
  userId: string,
  accessToken: string,
  targetOriginOverride?: string,
) => {
  const targetOrigin =
    targetOriginOverride ?? getPostMessageTargetOrigin();
  const messageData = {
    name: isDev() ? "select-stg:auth:login" : "select:auth:login",
    payload: JSON.stringify({
      user_id: userId,
      access_token: accessToken,
    }),
  };

  // Emit to Javascript Popup context
  if (window?.opener) {
    window.opener.postMessage?.(messageData, targetOrigin);
  }
  // If UX mode is with iframe:
  if (window?.parent && window.parent !== window) {
    window.parent.postMessage?.(messageData, targetOrigin);
  }
};

const detectUserCountry = async (): Promise<string> => {
  try {
    // Use a free IP geolocation service to detect user's country
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return data.country || 'US'; // Default to US if detection fails
  } catch (error) {
    console.error('Error detecting user country:', error);
    return 'US'; // Default to US if detection fails
  }
};

const isUserFromChina = async (): Promise<boolean> => {
  const country = await detectUserCountry();
  return country === 'CN'; // CN is the country code for China
};

export {
  getInfoFormIntlPhoneNumber,
  cls,
  isValidEmail,
  getDefaultLocale,
  getLanguageCode,
  getLanguage,
  getAgent,
  isVideo,
  isImage,
  setImageDimensions,
  setVideoDimensions,
  calVideoRatio,
  emitLoginSuccess,
  detectUserCountry,
  isUserFromChina,
};
